import React from 'react';
import { useHistory } from 'react-router-dom';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonCard,
  IonCardContent,
  IonButton,
  IonIcon,
  IonText,
  IonAlert,
  IonToggle,
} from '@ionic/react';
import {
  logOutOutline,
  personOutline,
  mailOutline,
  keyOutline,
  chevronForwardOutline,
  notificationsOutline,
  mapOutline,
  fingerPrintOutline,
  trashOutline,
  contrastOutline,
  phonePortraitOutline,
  sunnyOutline,
  moonOutline,
} from 'ionicons/icons';
import { useSettingsPage } from '../../application/use-cases/settings/useSettingsPage';
import { useThemePreference } from '../../application/use-cases/settings/useTheme';
import type { ThemePreference } from '../../infrastructure/browser/theme';
import './Settings.css';

const THEME_OPTIONS: { value: ThemePreference; label: string; icon: string }[] = [
  { value: 'system', label: 'Automático', icon: phonePortraitOutline },
  { value: 'light', label: 'Claro', icon: sunnyOutline },
  { value: 'dark', label: 'Oscuro', icon: moonOutline },
];

const Settings: React.FC = () => {
  const history = useHistory();
  const theme = useThemePreference();
  const {
    user,
    logout,
    showRename,
    setShowRename,
    renameError,
    setRenameError,
    pushUnsupported,
    pushEnabled,
    pushBusy,
    pushError,
    handleTogglePush,
    handleRename,
    passkeySupported,
    passkeys,
    passkeyBusy,
    passkeyError,
    handleEnrollPasskey,
    handleRemovePasskey,
  } = useSettingsPage();

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Ajustes</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen className="ion-padding">
        <div className="profile-card">
          <span className="profile-avatar">{(user?.name ?? '?').trim().charAt(0).toUpperCase()}</span>
          <span>
            <strong>{user?.name}</strong>
            <small>{user?.email}</small>
          </span>
        </div>
        <section className="settings-section">
          <p className="settings-eyebrow">Cuenta</p>
          <IonCard>
            <IonCardContent>
              <button
                className="settings-link-row"
                onClick={() => {
                  setRenameError(null);
                  setShowRename(true);
                }}
              >
                <IonIcon icon={personOutline} />
                {user?.name}
                <IonIcon icon={chevronForwardOutline} className="chevron" />
              </button>
              <div className="settings-row">
                <IonIcon icon={mailOutline} />
                {user?.email}
              </div>
              <button className="settings-link-row" onClick={() => history.push('/app/change-password')}>
                <IonIcon icon={keyOutline} />
                Cambiar contraseña
                <IonIcon icon={chevronForwardOutline} className="chevron" />
              </button>
            </IonCardContent>
          </IonCard>
          {renameError && (
            <IonText color="danger">
              <p style={{ fontSize: 13, margin: '6px 2px 0' }}>{renameError}</p>
            </IonText>
          )}
        </section>

        {passkeySupported && (
          <section className="settings-section">
            <p className="settings-eyebrow">Seguridad</p>
            <IonCard>
              <IonCardContent>
                <p className="settings-hint">
                  Activá el ingreso con huella o Face ID para entrar más rápido desde este dispositivo.
                </p>

                {passkeys.map((cred) => (
                  <div key={cred.id} className="settings-row settings-row-toggle">
                    <IonIcon icon={fingerPrintOutline} />
                    <span className="settings-row-toggle-label">{cred.deviceName}</span>
                    <button
                      className="delete-icon-btn"
                      onClick={() => handleRemovePasskey(cred.id)}
                      aria-label={`Quitar huella de ${cred.deviceName}`}
                    >
                      <IonIcon icon={trashOutline} />
                    </button>
                  </div>
                ))}

                <IonButton className="passkey-enroll" expand="block" fill="outline" onClick={handleEnrollPasskey} disabled={passkeyBusy}>
                  <IonIcon icon={fingerPrintOutline} slot="start" />
                  {passkeyBusy ? 'Un momento...' : 'Activar huella'}
                </IonButton>
                {passkeyError && (
                  <IonText color="danger">
                    <p className="settings-hint">{passkeyError}</p>
                  </IonText>
                )}
              </IonCardContent>
            </IonCard>
          </section>
        )}

        <section className="settings-section">
          <p className="settings-eyebrow">Alertas</p>
          <IonCard>
            <IonCardContent>
              <button className="settings-link-row" onClick={() => history.push('/app/alerts')}>
                <IonIcon icon={notificationsOutline} />
                Gestionar alertas
                <IonIcon icon={chevronForwardOutline} className="chevron" />
              </button>
            </IonCardContent>
          </IonCard>
        </section>

        <section className="settings-section">
          <p className="settings-eyebrow">Notificaciones</p>
          <IonCard>
            <IonCardContent>
              <div className="settings-row settings-row-toggle">
                <IonIcon icon={notificationsOutline} />
                <span className="settings-row-toggle-label">Avisos de alertas en este dispositivo</span>
                <IonToggle
                  checked={pushEnabled}
                  disabled={pushBusy || !!pushUnsupported}
                  onIonChange={(e) => handleTogglePush(e.detail.checked)}
                />
              </div>
              {pushUnsupported && <p className="settings-hint">{pushUnsupported}</p>}
              {pushError && (
                <IonText color="danger">
                  <p className="settings-hint">{pushError}</p>
                </IonText>
              )}
            </IonCardContent>
          </IonCard>
        </section>

        <section className="settings-section">
          <p className="settings-eyebrow">Apariencia</p>
          <IonCard>
            <IonCardContent>
              <div className="settings-row">
                <IonIcon icon={contrastOutline} />
                <span className="settings-row-toggle-label">Tema</span>
              </div>
              <div className="theme-picker" role="radiogroup" aria-label="Tema">
                {THEME_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    role="radio"
                    aria-checked={theme.preference === opt.value}
                    className={theme.preference === opt.value ? 'is-active' : ''}
                    onClick={() => theme.choose(opt.value)}
                  >
                    <IonIcon icon={opt.icon} />
                    {opt.label}
                  </button>
                ))}
              </div>
              <p className="settings-hint theme-hint">
                {theme.preference === 'system'
                  ? 'Sigue al teléfono: claro de día, oscuro si lo tenés activado.'
                  : 'Queda fijo en este dispositivo.'}
              </p>
            </IonCardContent>
          </IonCard>
        </section>

        <section className="settings-section">
          <p className="settings-eyebrow">Mapas</p>
          <IonCard>
            <IonCardContent>
              <button className="settings-link-row" onClick={() => history.push('/app/sites')}>
                <IonIcon icon={mapOutline} />
                Mis campos
                <IonIcon icon={chevronForwardOutline} className="chevron" />
              </button>
            </IonCardContent>
          </IonCard>
        </section>

        <IonButton expand="block" color="danger" fill="outline" onClick={logout}>
          <IonIcon icon={logOutOutline} slot="start" />
          Cerrar sesión
        </IonButton>

        <IonAlert
          isOpen={showRename}
          onDidDismiss={() => setShowRename(false)}
          header="Cambiar nombre"
          inputs={[{ name: 'name', type: 'text', value: user?.name, placeholder: 'Nombre' }]}
          buttons={[
            { text: 'Cancelar', role: 'cancel' },
            { text: 'Guardar', handler: (data) => handleRename(data.name ?? '') },
          ]}
        />
      </IonContent>
    </IonPage>
  );
};

export default Settings;
